import React from 'react';

import { DivProps, SpanProps } from './common-types';
import { Col, Row } from './Flex';
import { cn } from 'domain/core';

type CardProps = DivProps;
const Card = ({ className, ref, ...props }: CardProps) => {
	return (
		<Col
			className={cn(
				'h-auto min-h-[240px] w-full',
				'rounded border bg-neutral-50',
				'gap-0',
				className
			)}
			{...props}
		/>
	);
};

// Key and KeyValue

const KeyValue = ({ className, ref, ...props }: DivProps) => {
	return (
		<Col
			className={cn('align-start h-auto w-full justify-start gap-0', className)}
			{...props}
		/>
	);
};

const KeyValueItem = ({
	className,
	ref,
	...props
}: DivProps & {
	label: string;
	value: string | React.ReactNode;
}) => {
	return (
		<Row
			className={cn(
				'h-auto',
				'content-start	 justify-start self-start text-sm text-neutral-400',
				className
			)}
			{...props}>
			<Col className={cn('text-md basis-0 font-semibold text-neutral-800')}>
				{props.label}
			</Col>
			<Col
				className={cn(
					'text-md self-star align-start w-full justify-start text-right text-neutral-700'
				)}>
				{props.value}
			</Col>
		</Row>
	);
};

const AbstractTitle = ({ className, ref, ...props }: TitleProps) => {
	return (
		<Row
			className={cn(
				'h-auto w-full',
				'text-md justify-start text-neutral-400',
				className
			)}
			{...props}
		/>
	);
};

const Title = ({ className, ref, ...props }: TitleProps) => {
	return (
		<AbstractTitle
			className={cn('text-md text-neutral-800', 'truncate', className)}
			{...props}
		/>
	);
};

type TitleProps = DivProps;
const Subtitle = ({ className, ref, ...props }: TitleProps) => {
	return (
		<AbstractTitle
			className={cn('text-xs text-neutral-700', className)}
			{...props}
		/>
	);
};

const Titles = ({ className, ref, ...props }: DivProps) => {
	return (
		<Col
			className={cn(
				'align-start h-auto w-4/6 justify-start justify-start gap-0',
				className
			)}
			{...props}
		/>
	);
};

const Info = ({ className, ref, ...props }: DivProps) => {
	return (
		<Col
			className={cn('h-auto w-2/6 justify-end gap-0 text-sm', className)}
			{...props}
		/>
	);
};

const InfoItemLabel = ({ className, ref, ...props }: DivProps) => {
	return <Row className={cn('w-full', 'justify-end', className)} {...props} />;
};

type FooterProps = DivProps;
const Footer = ({ className, ref, ...props }: FooterProps) => {
	return (
		<Row
			className={cn('h-auto justify-end border-t pt-4', className)}
			{...props}
		/>
	);
};

type BodyProps = DivProps;
const Body = ({ className, ref, ...props }: BodyProps) => {
	return (
		<Col
			className={cn('h-auto w-full grow justify-start py-2 px-4', className)}
			{...props}
		/>
	);
};

const Header = ({ className, ref, ...props }: DivProps) => {
	return (
		<Row
			className={cn(
				'align-between h-auto w-full border-b py-2 px-4',
				className
			)}
			{...props}
		/>
	);
};

const Badge = ({
	className,
	ref,
	...props
}: SpanProps & { status: 'OK' | 'WARN' | 'ERROR' | 'UNKNOWN' }) => {
	const { status, ...rest } = props;

	/** 50 x 50 px badge (status indicator with color) */

	// Color is based on status
	const color =
		status === 'OK'
			? 'bg-primary-300'
			: status === 'WARN'
			? 'bg-orange-500'
			: status === 'ERROR'
			? 'bg-red-500'
			: 'bg-stone-500';

	return (
		<Row className={cn('rounded-full', 'h-8 w-auto', className)} {...rest}>
			<Row className={cn('rounded-full', 'h-3 w-3', color, className)} />
		</Row>
	);
};

Card.Title = Title;

Card.Header = Header;
Card.Badge = Badge;
Card.Titles = Titles;
Card.Subtitle = Subtitle;
Card.Info = Info;
Card.InfoItemLabel = InfoItemLabel;

Card.Body = Body;
Card.KeyValue = KeyValue;
Card.KeyValueItem = KeyValueItem;

Card.Footer = Footer;

export { Card };
